import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AUTH_EXPIRED_EVENT, completeSignupProfile } from './api';
import { loadRuntimeConfig, type RuntimeConfig } from './config';

interface TokenSet {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt: number;
}

interface AuthorizationFlow {
  verifier: string;
  state: string;
  screen: 'login' | 'signup';
  displayName?: string;
  createdAt: number;
  tokensExchanged?: boolean;
}

interface AuthContextValue {
  config: RuntimeConfig | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(): Promise<void>;
  signUp(displayName: string): Promise<void>;
  completeCallback(url: string): Promise<void>;
  resumeSignup(displayName?: string): Promise<void>;
  signOut(): Promise<void>;
}

const TOKEN_KEY = 'human-studio.tokens';
const PKCE_KEY = 'human-studio.pkce';
const AUTH_FLOW_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [tokens, setTokens] = useState<TokenSet | null>(() => readTokens());
  const [isLoading, setIsLoading] = useState(true);
  const expiryHandledRef = useRef(false);

  useEffect(() => {
    loadRuntimeConfig()
      .then(setConfig)
      .finally(() => setIsLoading(false));
  }, []);

  const startAuthorization = useCallback(async (screen: 'login' | 'signup', requestedDisplayName?: string) => {
    const currentConfig = config ?? (await loadRuntimeConfig());
    if (!currentConfig.cognitoDomain || !currentConfig.cognitoClientId) {
      throw new Error('Cognito configuration is missing');
    }
    const verifier = randomString(64);
    const state = randomString(40);
    const challenge = await sha256Base64Url(verifier);
    const displayName = requestedDisplayName?.trim();
    if (screen === 'signup' && (!displayName || displayName.length > 100)) {
      throw new Error('Enter a business or display name of 100 characters or fewer');
    }
    writeAuthorizationFlow({ verifier, state, screen, displayName, createdAt: Date.now() });
    const params = new URLSearchParams({
      client_id: currentConfig.cognitoClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: currentConfig.cognitoRedirectUri,
      state,
      code_challenge_method: 'S256',
      code_challenge: challenge
    });
    window.location.href = `${trimSlash(currentConfig.cognitoDomain)}/${screen === 'signup' ? 'signup' : 'oauth2/authorize'}?${params}`;
  }, [config]);
  const login = useCallback(() => startAuthorization('login'), [startAuthorization]);
  const signUp = useCallback((displayName: string) => startAuthorization('signup', displayName), [startAuthorization]);

  const resumeSignup = useCallback(async (requestedDisplayName?: string) => {
    const flow = readAuthorizationFlow();
    if (!flow || flow.screen !== 'signup' || !flow.tokensExchanged) {
      throw new Error('Your signup session has expired. Please start again.');
    }
    const displayName = requestedDisplayName?.trim() || flow.displayName;
    if (requestedDisplayName !== undefined && (!displayName || displayName.length > 100)) {
      throw new Error('Enter a business or display name of 100 characters or fewer');
    }
    if (displayName !== flow.displayName) {
      writeAuthorizationFlow({ ...flow, displayName });
    }
    const currentTokens = readTokens();
    const token = currentTokens?.idToken ?? currentTokens?.accessToken;
    if (!token || !currentTokens || currentTokens.expiresAt <= Date.now() + 30_000) {
      throw new Error('Your signup session has expired. Please start again.');
    }
    await completeSignupProfile(token, displayName);
    sessionStorage.removeItem(PKCE_KEY);
  }, []);

  const completeCallback = useCallback(
    async (url: string) => {
      const currentConfig = config ?? (await loadRuntimeConfig());
      const parsed = new URL(url);
      const code = parsed.searchParams.get('code');
      const returnedState = parsed.searchParams.get('state');
      const flow = readAuthorizationFlow();
      if (flow?.screen === 'signup' && flow.tokensExchanged) {
        await resumeSignup();
        return;
      }
      if (!code || !flow) {
        throw new Error('Your sign-in session has expired. Please start again.');
      }
      if (!returnedState || returnedState !== flow.state) {
        throw new Error('The sign-in response could not be verified. Please start again.');
      }

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: currentConfig.cognitoClientId,
        code,
        redirect_uri: currentConfig.cognitoRedirectUri,
        code_verifier: flow.verifier
      });

      const response = await fetch(`${trimSlash(currentConfig.cognitoDomain)}/oauth2/token`, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body
      });
      if (!response.ok) {
        throw new Error('Could not complete sign in');
      }
      const payload = (await response.json()) as {
        access_token: string;
        id_token?: string;
        refresh_token?: string;
        expires_in: number;
      };
      const nextTokens: TokenSet = {
        accessToken: payload.access_token,
        idToken: payload.id_token,
        refreshToken: payload.refresh_token,
        expiresAt: Date.now() + payload.expires_in * 1000
      };
      localStorage.setItem(TOKEN_KEY, JSON.stringify(nextTokens));
      expiryHandledRef.current = false;
      setTokens(nextTokens);
      if (flow.screen === 'signup') {
        writeAuthorizationFlow({ ...flow, tokensExchanged: true });
        await completeSignupProfile(nextTokens.idToken ?? nextTokens.accessToken, flow.displayName);
      }
      sessionStorage.removeItem(PKCE_KEY);
    },
    [config, resumeSignup]
  );

  const signOut = useCallback(async () => {
    expiryHandledRef.current = true;
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(PKCE_KEY);
    setTokens(null);
    const currentConfig = config ?? (await loadRuntimeConfig());
    if (currentConfig.cognitoDomain && currentConfig.cognitoClientId) {
      const params = new URLSearchParams({
        client_id: currentConfig.cognitoClientId,
        logout_uri: window.location.origin
      });
      window.location.href = `${trimSlash(currentConfig.cognitoDomain)}/logout?${params}`;
    }
  }, [config]);

  const expireAuthentication = useCallback(() => {
    if (expiryHandledRef.current) {
      return;
    }
    expiryHandledRef.current = true;
    void signOut();
  }, [signOut]);

  useEffect(() => {
    const handleAuthExpired = () => expireAuthentication();
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, [expireAuthentication]);

  useEffect(() => {
    if (!tokens) {
      return;
    }
    const expiresIn = tokens.expiresAt - Date.now() - 30_000;
    if (expiresIn <= 0) {
      expireAuthentication();
      return;
    }
    const timeoutId = window.setTimeout(expireAuthentication, expiresIn);
    return () => window.clearTimeout(timeoutId);
  }, [expireAuthentication, tokens]);

  const value = useMemo<AuthContextValue>(() => {
    const validTokens = tokens && tokens.expiresAt > Date.now() + 30_000 ? tokens : null;
    return {
      config,
      token: validTokens?.idToken ?? validTokens?.accessToken ?? null,
      isAuthenticated: Boolean(validTokens),
      isLoading,
      login,
      signUp,
      completeCallback,
      resumeSignup,
      signOut
    };
  }, [completeCallback, config, isLoading, login, resumeSignup, signOut, signUp, tokens]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}

function readTokens(): TokenSet | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) {
      return null;
    }
    const tokens = JSON.parse(raw) as TokenSet;
    if (typeof tokens.accessToken !== 'string' || typeof tokens.expiresAt !== 'number') {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return tokens;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

function readAuthorizationFlow(): AuthorizationFlow | null {
  try {
    const raw = sessionStorage.getItem(PKCE_KEY);
    if (!raw) return null;
    const flow = JSON.parse(raw) as AuthorizationFlow;
    if (
      typeof flow.verifier !== 'string' ||
      typeof flow.state !== 'string' ||
      (flow.screen !== 'login' && flow.screen !== 'signup') ||
      typeof flow.createdAt !== 'number' ||
      Date.now() - flow.createdAt > AUTH_FLOW_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(PKCE_KEY);
      return null;
    }
    return flow;
  } catch {
    sessionStorage.removeItem(PKCE_KEY);
    return null;
  }
}

function writeAuthorizationFlow(flow: AuthorizationFlow): void {
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(flow));
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => (byte % 36).toString(36)).join('');
}

async function sha256Base64Url(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
